import type { QueryView } from '@krp/client-runtime'

import type { ConfidencePart } from '../../search/model/types'

/**
 * Модель вкладки «Тестирование», вид «Поиск по фото» (RP-FB29). Данные — ответы
 * сервера замеров (`Platform.Contracts/Testing`, RP-FB28) как есть; vm добавляет
 * к ним только слова и проценты для экрана.
 */

export const TESTING_LANES = ['all', 'visual', 'text', 'barcode'] as const
export type TestingLane = (typeof TESTING_LANES)[number]

export const isTestingLane = (value: string): value is TestingLane => TESTING_LANES.some((lane) => lane === value)

/** Оси пометок кадра — те же, что у сервера и стенда `search/eval`. */
export const TESTING_TAG_AXES = ['angle', 'quality', 'light', 'distance', 'occlusion', 'packaging', 'barcode'] as const
export type TestingTagAxis = (typeof TESTING_TAG_AXES)[number]

export interface TestProduct {
  id: string
  name: string
  sku: string | null
  barcodes: readonly string[]
  source: { productId: string; copiedAt: string } | null
  references: number
  frames: number
  indexState: 'indexed' | 'failed'
  indexError: string | null
  version: number
  updatedAt: string
}

export interface TestPhoto {
  id: string
  role: 'reference' | 'frame'
  origin: 'manual' | 'source'
  contentType: string
  bytes: number
  tags: Readonly<Record<string, string>>
  addedAt: string
}

export interface TestProductDetail {
  product: TestProduct
  references: readonly TestPhoto[]
  frames: readonly TestPhoto[]
}

export interface TestingHit {
  rank: number
  productId: string
  name: string
  /** Уверенность 0…1 (RP-FB54): та же формула и тот же порядок, что в боевом поиске. */
  confidence: number
  confidenceParts: readonly ConfidencePart[]
  points: number
  matchedBy: readonly string[]
}

export interface TestingLaneResult {
  lane: TestingLane
  expectedRank: number | null
  expectedScore: number | null
  margin: number | null
  hits: readonly TestingHit[]
}

export interface TestingCheck {
  expectedProductId: string | null
  recognized: { text: string | null; barcodes: readonly string[] } | null
  lanes: readonly TestingLaneResult[]
  latencyMs: number
}

export interface TestingProportion {
  successes: number
  n: number
  value: number | null
  low: number | null
  high: number | null
}

export interface TestingMean {
  n: number
  value: number | null
  low: number | null
  high: number | null
}

export interface TestingLaneMetrics {
  lane: TestingLane
  frames: number
  top1: TestingProportion
  top3: TestingProportion
  top5: TestingProportion
  top10: TestingProportion
  notFound: TestingProportion
  mrr: TestingMean
  positionScore: TestingMean
  expectedScore: TestingMean
  margin: TestingMean
}

export interface TestingProductSlice {
  productId: string
  name: string
  frames: number
  top1: TestingProportion
  top5: TestingProportion
  positionScore: TestingMean
}

export interface TestingTagSlice {
  axis: string
  value: string
  frames: number
  top1: TestingProportion
  top5: TestingProportion
  positionScore: TestingMean
}

export interface TestingConfusion {
  expectedProductId: string
  expectedName: string
  insteadProductId: string
  insteadName: string
  count: number
}

export interface TestingRunMetrics {
  lanes: readonly TestingLaneMetrics[]
  products: readonly TestingProductSlice[]
  tags: readonly TestingTagSlice[]
  confusions: readonly TestingConfusion[]
  latency: { n: number; medianMs: number | null; p95Ms: number | null }
  errors: number
}

export interface TestingRunStamp {
  encoderModel: string | null
  encoderDim: number | null
  platformVersion: string
  rrfK: number
  visualWeight: number
  textWeight: number
  barcodeWeight: number
  products: number
  frames: number
  /** Версия и числа формулы уверенности; `null` — прогон посчитан до неё (прежним коэффициентом). */
  confidenceFormula: string | null
  /** Режим подготовки кадра (`none` / `background-removed`, RP-FB62); `null` — прогон до режимов, то есть без удаления фона. */
  photoPrep?: string | null | undefined
}

export const TESTING_RUN_STATES = ['queued', 'running', 'done', 'failed', 'cancelled'] as const
export type TestingRunState = (typeof TESTING_RUN_STATES)[number]

export const isTestingRunState = (value: string): value is TestingRunState => TESTING_RUN_STATES.some((state) => state === value)

export interface TestingRun {
  id: string
  state: TestingRunState
  lanes: string
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  totalFrames: number
  doneFrames: number
  error: string | null
  createdBy: string
  stamp: TestingRunStamp | null
  metrics: TestingRunMetrics | null
}

export interface TestingMetricDelta {
  metric: string
  previous: number | null
  current: number | null
  delta: number | null
}

export interface TestingRunDetail {
  run: TestingRun
  comparison: { previousRunId: string; sameStamp: boolean; deltas: readonly TestingMetricDelta[] } | null
}

export interface TestingFrameOutcome {
  frameId: string
  productId: string
  productName: string
  tags: Readonly<Record<string, string>>
  recognizedText: string | null
  latencyMs: number | null
  error: string | null
  lanes: readonly {
    lane: TestingLane
    rank: number | null
    score: number | null
    margin: number | null
    topProductId: string | null
    topName: string | null
  }[]
}

/** Что человек вводит в карточке товара: штрихкоды — строкой через запятую или с новой строки. */
export interface TestProductDraft {
  name: string
  sku: string
  barcodes: string
}

// --- vm ---------------------------------------------------------------------------------------------------------

/** Состояние словами и значком, а не только цветом (CLAUDE.md §7). */
export interface StateBadgeVm {
  tone: 'ok' | 'busy' | 'bad' | 'muted'
  text: string
}

/** Доля или среднее словами: «72 %» и «95 %: 61–81 %, n = 50»; без наблюдений — «нет данных». */
export interface ShareVm {
  value: string
  detail: string
}

/** Права человека на вкладку: пока не известны — ждём, без права — «нет доступа», а не пустой экран. */
export type TestingAccess = 'unknown' | 'allowed' | 'denied'

/** Чей экран тестовых товаров: товары общие, но поиску по тексту фото не нужны (RP-FB34). */
export type TestingKind = 'photo' | 'text'

export interface TestProductRowVm {
  id: string
  name: string
  sku: string | null
  barcodes: string
  photos: string
  index: StateBadgeVm
  sourceText: string | null
}

export interface CatalogPickVm {
  query: QueryView | null
  items: readonly { id: string; name: string; sku: string | null }[]
}

export interface TestProductsVm {
  query: QueryView
  rows: readonly TestProductRowVm[]
  catalog: CatalogPickVm
  action: MutationStateVm
}

export interface TestPhotoVm {
  id: string
  url: string
  originText: string
  tagsText: string
}

export interface TestProductVm {
  query: QueryView
  product: TestProduct | null
  draft: TestProductDraft | null
  index: StateBadgeVm | null
  sourceText: string | null
  references: readonly TestPhotoVm[]
  frames: readonly TestPhotoVm[]
  action: MutationStateVm
  /** Копия товара каталога, а фото из источника не пришло: почему эталонов нет и что делать. */
  sourcePhotosNote: string | null
}

export interface TestingHitVm {
  rank: number
  name: string
  /** «87%». */
  confidence: string
  /** «Фото +29, Текст +50» — из чего сложилась уверенность; пусто, если вкладов нет. */
  parts: string
  matchedBy: string
  expected: boolean
}

export interface TestingLaneVm {
  lane: TestingLane
  title: string
  verdict: StateBadgeVm
  margin: string | null
  hits: readonly TestingHitVm[]
}

export interface TestingCheckVm {
  busy: boolean
  error: MutationStateVm['error']
  recognized: string | null
  latency: string | null
  lanes: readonly TestingLaneVm[]
}

export interface TestingRunRowVm {
  id: string
  state: StateBadgeVm
  lanesText: string
  when: string
  progress: string
  top1: ShareVm | null
  top5: ShareVm | null
  cancellable: boolean
}

export interface TestingRunsVm {
  query: QueryView
  rows: readonly TestingRunRowVm[]
  /** Последний завершённый прогон: балл крупно рядом с оговоркой о тестовом каталоге. */
  latest: { id: string; title: string; scores: readonly { label: string; share: ShareVm }[] } | null
  /** Почему новый замер сейчас не запустить (идёт свой или замер другого вида — очередь общая); `null` — можно. */
  running: string | null
  action: MutationStateVm
}

export interface TestingLaneCellsVm {
  lane: TestingLane
  title: string
  frames: number
  cells: readonly { label: string; share: ShareVm }[]
}

export interface TestingRunVm {
  query: QueryView
  title: string | null
  state: StateBadgeVm | null
  progress: string | null
  active: boolean
  error: string | null
  lanes: readonly TestingLaneCellsVm[]
  products: readonly { productId: string; name: string; frames: number; top1: ShareVm; top5: ShareVm; position: ShareVm }[]
  tags: readonly { key: string; axis: string; value: string; frames: number; top1: ShareVm; top5: ShareVm }[]
  confusions: readonly { key: string; expectedProductId: string; text: string; count: number }[]
  latency: string | null
  errors: number
  stamp: readonly { label: string; value: string }[]
  comparison: { previousRunId: string; note: string | null; rows: readonly { label: string; previous: string; current: string; delta: string }[] } | null
  action: MutationStateVm
}

/** Состояние команды: идёт ли и причина отказа сервера дословно (ADR-8). */
export interface MutationStateVm {
  busy: boolean
  error: { kind: string; code: string; message: string; correlationId: string | null } | null
}

export interface TestingHomeActions {
  openPhotoSearch: () => void
  openTextSearch: () => void
  openTwin: () => void
}

export interface TestProductsActions {
  reload: () => void
  create: (draft: TestProductDraft) => void
  searchCatalog: (query: string) => void
  copyFromCatalog: (catalogProductId: string) => void
  open: (productId: string) => void
  remove: (productId: string) => void
  openRuns: () => void
}

export interface TestProductActions {
  reload: () => void
  save: (draft: TestProductDraft) => void
  refreshFromSource: () => void
  reindex: () => void
  addPhoto: (role: 'reference' | 'frame', file: File, tags: Readonly<Record<string, string>>) => void
  removePhoto: (photoId: string) => void
  checkFrame: (photoId: string) => void
  remove: () => void
}

export interface TestingCheckActions {
  recheck: () => void
}

export interface TestingRunsActions {
  reload: () => void
  start: (lanes: TestingLane) => void
  cancel: (runId: string) => void
  open: (runId: string) => void
  openProducts: () => void
}

export interface TestingRunActions {
  reload: () => void
  cancel: () => void
  openProduct: (productId: string) => void
  openRun: (runId: string) => void
}

// --- поиск по тексту (RP-FB34) ------------------------------------------------------------------------------------

/** Вид запроса — что именно он проверяет; те же значения, что у сервера (`TextQueryKinds`). */
export const TEXT_QUERY_KINDS = ['name', 'part', 'typo', 'layout', 'sku', 'barcode', 'synonym', 'other'] as const
export type TextQueryKind = (typeof TEXT_QUERY_KINDS)[number]

export const isTextQueryKind = (value: string): value is TextQueryKind => TEXT_QUERY_KINDS.some((kind) => kind === value)

/** Ступень поиска номенклатуры, на которой закончился запрос (`TextSearchSteps`). */
export const TEXT_SEARCH_STEPS = ['barcode', 'sku', 'barcode-like', 'service', 'empty'] as const
export type TextSearchStep = (typeof TEXT_SEARCH_STEPS)[number]

export const isTextSearchStep = (value: string): value is TextSearchStep => TEXT_SEARCH_STEPS.some((step) => step === value)

export interface TextQuery {
  id: string
  text: string
  kind: TextQueryKind
  origin: 'generated' | 'manual'
  productId: string
  productName: string
  createdAt: string
}

export interface TextCheckHit {
  rank: number
  productId: string
  name: string
  sku: string | null
  /** Чем совпал: `barcode`, `sku`, `all_words`, `some_words`, `semantic`; `null` — служба не объяснила. */
  matchedBy: string | null
}

export interface TextCheck {
  query: string
  expectedProductId: string | null
  step: TextSearchStep
  expectedRank: number | null
  expectedMatchedBy: string | null
  hits: readonly TextCheckHit[]
  latencyMs: number
}

export interface TextQueryOutcome {
  queryId: string
  text: string
  kind: string
  productId: string
  productName: string
  step: string | null
  matchedBy: string | null
  rank: number | null
  above: readonly string[]
  latencyMs: number | null
  error: string | null
}

export interface TextKindSlice {
  kind: string
  queries: number
  top1: TestingProportion
  top5: TestingProportion
  notFound: TestingProportion
  positionScore: TestingMean
}

export interface TextRunMetrics {
  overall: Omit<TestingLaneMetrics, 'lane'>
  kinds: readonly TextKindSlice[]
  matches: readonly { matchedBy: string; queries: number }[]
  worst: readonly TextQueryOutcome[]
  latency: { n: number; medianMs: number | null; p95Ms: number | null }
  errors: number
}

export interface TextRunStamp {
  platformVersion: string
  textGeneration: string | null
  products: number
  queries: number
}

export interface TextRun {
  id: string
  state: TestingRunState
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  totalQueries: number
  doneQueries: number
  error: string | null
  createdBy: string
  stamp: TextRunStamp | null
  metrics: TextRunMetrics | null
}

export interface TextRunDetail {
  run: TextRun
  comparison: TestingRunDetail['comparison']
}

/** Свой запрос в форме: текст, тестовый товар и вид. */
export interface TextQueryDraft {
  text: string
  productId: string
  kind: TextQueryKind
}

export interface TextQueryRowVm {
  id: string
  text: string
  kind: TextQueryKind
  kindTitle: string
  originText: string
  productName: string
}

export interface TextQueriesVm {
  query: QueryView
  rows: readonly TextQueryRowVm[]
  /** Тестовые товары для выбора «какой должен найтись». */
  products: readonly { id: string; name: string }[]
  /** Сколько запросов каждого вида (для отбора). */
  counts: Readonly<Record<string, number>>
  generated: string | null
  action: MutationStateVm
}

export interface TextHitVm {
  rank: number
  name: string
  sku: string | null
  matchedBy: string
  expected: boolean
}

export interface TextCheckVm {
  busy: boolean
  error: MutationStateVm['error']
  verdict: StateBadgeVm | null
  step: string | null
  latency: string | null
  hits: readonly TextHitVm[]
  /** Что искали и какой товар ждали — для «Сохранить как запрос». */
  checked: { query: string; expectedProductId: string | null } | null
}

export interface TextRunRowVm {
  id: string
  state: StateBadgeVm
  when: string
  progress: string
  top1: ShareVm | null
  top5: ShareVm | null
  cancellable: boolean
}

export interface TextRunsVm {
  query: QueryView
  rows: readonly TextRunRowVm[]
  latest: { id: string; title: string; scores: readonly { label: string; share: ShareVm }[] } | null
  /** Почему новый замер сейчас не запустить (идёт свой или замер другого вида — очередь общая); `null` — можно. */
  running: string | null
  action: MutationStateVm
}

export interface TextRunVm {
  query: QueryView
  title: string | null
  state: StateBadgeVm | null
  progress: string | null
  active: boolean
  error: string | null
  scores: readonly { label: string; share: ShareVm }[]
  kinds: readonly { kind: string; title: string; queries: number; top1: ShareVm; top5: ShareVm; notFound: ShareVm }[]
  matches: readonly { key: string; title: string; queries: number }[]
  worst: readonly { queryId: string; text: string; kindTitle: string; productName: string; place: StateBadgeVm; detail: string }[]
  latency: string | null
  errors: number
  stamp: readonly { label: string; value: string }[]
  comparison: TestingRunVm['comparison']
  action: MutationStateVm
}

export interface TextQueriesActions {
  reload: () => void
  generate: () => void
  /** Сохранить свой запрос; `true` — сервер принял, поле можно очистить. */
  add: (draft: TextQueryDraft) => Promise<boolean>
  remove: (queryId: string) => void
  check: (queryId: string) => void
  openCheck: () => void
  openRuns: () => void
  openProducts: () => void
}

export interface TextCheckActions {
  check: (query: string, expectedProductId: string | null) => void
  save: (draft: TextQueryDraft) => void
  openQueries: () => void
}

export interface TextRunsActions {
  reload: () => void
  start: () => void
  cancel: (runId: string) => void
  open: (runId: string) => void
  openQueries: () => void
}

export interface TextRunActions {
  reload: () => void
  cancel: () => void
  check: (queryId: string) => void
  openRun: (runId: string) => void
}
